import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-hot-toast";
import {
  decrementOrderQuantity,
  getMoqFromInventory,
  incrementOrderQuantity,
  normalizeOrderQuantity,
} from "../../utils/orderQuantity";

const initialState = {
  cartItems: localStorage.getItem("cartItems")
    ? JSON.parse(localStorage.getItem("cartItems"))
    : [],
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
};

export const CartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      if (productIndex >= 0) {
        state.cartItems[productIndex].cartQuantity += 1;

        // Format attributes and capitalize product name
        const attributesFormatted = Object.entries(state.cartItems[productIndex].attributes)
          .filter(([_, value]) => Boolean(value)) // Filters out falsy values
          .map(([_, value]) => `${value}`)
          .join(' - ');


        const productName = state.cartItems[productIndex]?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

        toast.success(`"${capitalizedProductName} - ${attributesFormatted}" quantity updated in cart!`);
      } else {
        const tempProduct = { ...action.payload, cartQuantity: 1 };
        state.cartItems.push(tempProduct);

        // Format attributes and capitalize product name
        const attributesFormatted = Object.entries(tempProduct.attributes)
          .filter(([_, value]) => Boolean(value)) // Filters out falsy values
          .map(([_, value]) => `${value}`)
          .join(' - ');


        const productName = tempProduct?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

        toast.success(`"${capitalizedProductName} - ${attributesFormatted}" added to cart!`);
      }
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    removeFromCart(state, action) {
      const removedItem = state.cartItems.find(
        (item) => item.id === action.payload.id
      );
      if (removedItem) {
        const nextCartItems = state.cartItems.filter(
          (item) => item.id !== action.payload.id
        );
        state.cartItems = nextCartItems;

        // Format attributes and capitalize product name
        const attributesFormatted = Object.entries(removedItem.attributes)
          .filter(([_, value]) => Boolean(value)) // Filters out falsy values
          .map(([_, value]) => `${value}`)
          .join(' - ');


        const productName = removedItem?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

        toast.success(`"${capitalizedProductName} - ${attributesFormatted}" removed from cart!`);
      }
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    decreaseCart(state, action) {
      const itemIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );
      const currentItem = state.cartItems[itemIndex];
      const moq = getMoqFromInventory(currentItem);
      const nextQty = decrementOrderQuantity(
        currentItem.cartQuantity,
        moq,
        moq
      );

      if (currentItem.cartQuantity > moq) {
        state.cartItems[itemIndex].cartQuantity = nextQty;

        // Format attributes and capitalize product name
        const attributesFormatted = Object.entries(state.cartItems[itemIndex].attributes)
          .filter(([_, value]) => Boolean(value)) // Filters out falsy values
          .map(([_, value]) => `${value}`)
          .join(' - ');


        const productName = state.cartItems[itemIndex]?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

        toast.success(`"${capitalizedProductName} - ${attributesFormatted}" quantity decreased in cart!`);
      } else {
        const nextCartItems = state.cartItems.filter(
          (item) => item.id !== action.payload.id
        );
        state.cartItems = nextCartItems;

        // Capitalize product name
        const productName = action.payload?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

        toast.success(`"${capitalizedProductName}" removed from cart!`);
      }
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    incrementCart(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.id
      );

      if (productIndex >= 0) {
        const currentItem = state.cartItems[productIndex];

        const moq = getMoqFromInventory(currentItem);
        const nextQty = incrementOrderQuantity(
          currentItem.cartQuantity,
          moq,
          currentItem.stock
        );

        if (nextQty > currentItem.cartQuantity) {
          currentItem.cartQuantity = nextQty;

          // Format attributes and capitalize product name
          const attributesFormatted = Object.entries(currentItem.attributes)
            .filter(([_, value]) => Boolean(value))
            .map(([_, value]) => `${value}`)
            .join(' - ');

          const productName = currentItem?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

          toast.success(`"${capitalizedProductName} - ${attributesFormatted}" quantity increased in cart!`);
        } else {
          // Stock caps every variant; back order doesn't lift it (#184).
          const productName = currentItem?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.error(`Cannot add more than ${currentItem.stock} items of "${capitalizedProductName}"!`);
        }
      } else {
        if (action.payload.stock > 0) {
          const moq = getMoqFromInventory(action.payload);
          const tempProduct = {
            ...action.payload,
            cartQuantity: moq,
          };
          state.cartItems.push(tempProduct);

          // Format attributes and capitalize product name
          const attributesFormatted = Object.entries(tempProduct.attributes)
            .filter(([_, value]) => Boolean(value))
            .map(([_, value]) => `${value}`)
            .join(' - ');

          const productName = tempProduct?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);

          toast.success(`"${capitalizedProductName} - ${attributesFormatted}" added to cart!`);
        } else {
          const productName = action.payload?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.error(`"${capitalizedProductName}" is out of stock!`);
        }
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    addByIncrement(state, action) {
      const productIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload.product.id
      );

      if (productIndex >= 0) {
        const currentItem = state.cartItems[productIndex];
        const moq = getMoqFromInventory(currentItem);
        const requestedQty = normalizeOrderQuantity(
          action.payload.cartQuantity,
          {
            step: moq,
            min: moq,
            max: currentItem.stock,
          }
        );
        const newQty = normalizeOrderQuantity(
          currentItem.cartQuantity + requestedQty,
          {
            step: moq,
            min: moq,
            max: currentItem.stock,
          }
        );
        const availableStock = currentItem.stock;

        if (newQty <= availableStock) {
          currentItem.cartQuantity = newQty;
          const attributesFormatted = Object.entries(currentItem.attributes)
            .filter(([_, value]) => Boolean(value))
            .map(([_, value]) => `${value}`)
            .join(' - ');
          const productName = currentItem?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.success(`"${capitalizedProductName} - ${attributesFormatted}" quantity updated in cart!`);
        } else {
          const cappedQty = normalizeOrderQuantity(availableStock, {
            step: moq,
            min: moq,
            max: availableStock,
          });
          currentItem.cartQuantity = cappedQty;
          const attributesFormatted = Object.entries(currentItem.attributes)
            .filter(([_, value]) => Boolean(value))
            .map(([_, value]) => `${value}`)
            .join(' - ');

          const productName = currentItem?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.error(`Only ${availableStock} items available for "${capitalizedProductName} - ${attributesFormatted}"!`);
        }
      } else {
        const product = action.payload.product;
        const moq = getMoqFromInventory(product);
        const requestedQty = normalizeOrderQuantity(
          action.payload.cartQuantity || moq,
          {
            step: moq,
            min: moq,
            max: product.stock,
          }
        );
        const availableStock = product.stock;

        if (availableStock <= 0) {
          const productName = product?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.error(`"${capitalizedProductName}" is out of stock!`);
          return;
        }

        const actualQty = normalizeOrderQuantity(requestedQty, {
          step: moq,
          min: moq,
          max: availableStock,
        });
        const tempProduct = {
          ...product,
          cartQuantity: actualQty
        };

        if (actualQty < requestedQty) {
          const attributesFormatted = Object.entries(product.attributes)
            .filter(([_, value]) => Boolean(value))
            .map(([_, value]) => `${value}`)
            .join(' - ');

          const productName = product?.properties?.product_info?.name;
          const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
          toast.error(`Only ${availableStock} items available for "${capitalizedProductName} - ${attributesFormatted}"!`);
        }

        state.cartItems.push(tempProduct);
        const attributesFormatted = Object.entries(tempProduct.attributes)
          .filter(([_, value]) => Boolean(value))
          .map(([_, value]) => `${value}`)
          .join(' - ');

        const productName = tempProduct?.properties?.product_info?.name;
        const capitalizedProductName = productName?.charAt(0).toUpperCase() + productName?.slice(1);
        toast.success(`"${capitalizedProductName} - ${attributesFormatted}" added to cart!`);
      }

      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
    },

    // Replace the whole cart from the server-saved cart (login / app start).
    // No toasts — this is background sync, not a user action.
    replaceCart(state, action) {
      state.cartItems = action.payload;
      localStorage.setItem("cartItems", JSON.stringify(state.cartItems));
      let { total, quantity } = state.cartItems.reduce(
        (cartTotal, cartItem) => {
          cartTotal.total += cartItem.price * cartItem.cartQuantity;
          cartTotal.quantity += cartItem.cartQuantity;
          return cartTotal;
        },
        { total: 0, quantity: 0 }
      );
      state.cartTotalQuantity = quantity;
      state.cartTotalAmount = parseFloat(total.toFixed(2));
    },

    clearCart(state) {
      state.cartItems = [];
      state.cartTotalQuantity = 0;
      state.cartTotalAmount = 0;
      localStorage.removeItem("cartItems");
    },

    getTotals(state, action) {
      let { total, quantity } = state.cartItems.reduce(
        (cartTotal, cartItem) => {
          const { price, cartQuantity } = cartItem;
          const itemTotal = price * cartQuantity;
          cartTotal.total += itemTotal;
          cartTotal.quantity += cartQuantity;
          return cartTotal;
        },
        {
          total: 0,
          quantity: 0,
        }
      );
      total = parseFloat(total.toFixed(2));
      state.cartTotalQuantity = quantity;
      state.cartTotalAmount = total;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  decreaseCart,
  incrementCart,
  addByIncrement,
  replaceCart,
  getTotals,
  clearCart,
} = CartSlice.actions;

export const cartReducer = CartSlice.reducer;